import { RootState } from 'gf-types';
import { DateTimeFormatInfo, getRawDateTimeFormatInfo } from '../../helpers/DateTimeUtility';
import { getFieldFeatures } from '../helpers';
import { FieldFeatures, KeyPath, TemplateEditorActionDispatcher } from '../templateeditor-types';
import { AiTemplateBuilderPayload, AiTemplateEditorAction, DetectedField } from './templateeditor-ai-types';
import { getTemplateState } from '../selectors';
import { AiFieldMetrics, AiFieldMetricState } from '../../types/template-types';
import { analyticsTrack } from '../../helpers/AnalyticsUtility';
import { raygunSendError } from '../../helpers/RaygunUtility';

export type AiTemplateBuilderAction = {
  type: string;
  payload: AiTemplateBuilderPayload;
};

export const TEMPLATE_EDITOR_AI_SAVED_WITH_AI_FIELDS_SUCCESS = 'TEMPLATE_EDITOR_AI_SAVED_WITH_AI_FIELDS_SUCCESS';
export const TEMPLATE_EDITOR_AI_ANALYZE_REQUEST = 'TEMPLATE_EDITOR_AI_ANALYZE_REQUEST';
export const TEMPLATE_EDITOR_AI_ANALYZE_TRACKED = 'TEMPLATE_EDITOR_AI_ANALYZE_TRACKED';
export const TEMPLATE_EDITOR_AI_APPLY_FIELD_SUGGESTION = 'TEMPLATE_EDITOR_AI_APPLY_FIELD_SUGGESTION';
export const TEMPLATE_EDITOR_AI_APPLY_CHECKBOX_GROUPING = 'TEMPLATE_EDITOR_AI_APPLY_CHECKBOX_GROUPING';

export const applyFieldSuggestion = (
  pageKeyPath: KeyPath,
  field: DetectedField,
  features?: FieldFeatures,
  rawDateFormat?: DateTimeFormatInfo,
  batchUndoId?: string
): AiTemplateEditorAction => {
  const featuresToUse = features ?? getFieldFeatures();
  const rawDateFormatToUse = rawDateFormat ?? getRawDateTimeFormatInfo();

  return {
    type: TEMPLATE_EDITOR_AI_APPLY_FIELD_SUGGESTION,
    undoable: true,
    batchUndoId,
    payload: { fieldSuggestion: { pageKeyPath, field, features: featuresToUse, rawDateFormat: rawDateFormatToUse } }
  };
};

export const applyCheckboxGrouping = (batchUndoId?: string): AiTemplateEditorAction => ({
  type: TEMPLATE_EDITOR_AI_APPLY_CHECKBOX_GROUPING,
  undoable: true,
  batchUndoId: batchUndoId
});

export const trackAiMetrics = () => (dispatch: TemplateEditorActionDispatcher, getState: () => RootState) => {
  const state = getTemplateState(getState());
  const { aiMetrics } = state.template;

  // do nothing if the ai metrics are not set up
  if (!aiMetrics) {
    return;
  }

  // also, don't send anything if the analyze count is 0
  if (aiMetrics.analyzeCount === 0) {
    return;
  }

  try {
    // gather the AI metrics to track
    const { analyzeCount, saveCount, fieldMetrics } = aiMetrics;
    // count the number field changes related to ai
    // look at each prop in the field metrics and reduce the individual counts
    // of the props that are set to Changed into a new object of props to their tracked counts
    const aiFieldChanges = Object.values(fieldMetrics)
      .flatMap((metrics) => Object.entries(metrics))
      .filter(([, metricState]) => metricState === AiFieldMetricState.Changed)
      .reduce((fieldChanges, [prop]) => {
        fieldChanges[prop] = (fieldChanges[prop] || 0) + 1;

        return fieldChanges;
      }, {} as Record<keyof AiFieldMetrics, number>);

    analyticsTrack('AI Fields Saved Metrics', {
      analyzeCount,
      saveCount,
      ...aiFieldChanges
    });

    // dispatch so we can update the ai metrics to tracked
    dispatch({ type: TEMPLATE_EDITOR_AI_ANALYZE_TRACKED });
  } catch (error) {
    // swallow
    raygunSendError(error, { logToConsole: true });
  }
};
